// Copyright 2026 Zibby Lab. Apache-2.0.
//
// SplitButton — atomic split button + dropdown menu.
//
// One white-pill control with a primary action (main button) and a
// chevron that opens a menu of alternative quantities/options. Same
// visual as the marketplace deploy wizard's footer button so the
// pattern is consistent across the app.
//
// Usage:
//   <SplitButton
//     primaryLabel="Deploy"
//     primaryIcon={cloudUploadOutline}
//     onPrimary={() => deploy({ warm: 0 })}
//     menuHeader={{ icon: flashOutline, label: 'Warm pool' }}
//     menuHelp="Skip ~60s cold start by keeping Fargate tasks pre-warmed."
//     options={[
//       { value: 1, label: 'Deploy with 1 warm worker',  hint: '$0.03/hr' },
//       { value: 2, label: 'Deploy with 2 warm workers', hint: '$0.06/hr' },
//       { value: 3, label: 'Deploy with 3 warm workers', hint: '$0.09/hr' },
//     ]}
//     onSelect={(n) => deploy({ warm: n })}
//     disabled={deploying}
//     menuDirection="up"     // 'up' (default, anchored bottom) or 'down'
//   />
//
// The primary button is its own click target — it does NOT go through
// the options list. Use `onPrimary` for the "default" action and
// `onSelect` for the per-option choices.

import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { IonIcon } from '@ionic/react';
import { chevronDownOutline } from 'ionicons/icons';
import './SplitButton.css';

const SplitButton = ({
  primaryLabel,
  primaryIcon,
  onPrimary,
  disabled = false,
  menuHeader,
  menuHelp,
  options,
  onSelect,
  menuDirection = 'up',
  size = 'md',
  className = '',
  primaryTitle,
  ariaLabel,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const rootRef = useRef(null);

  // Close on outside click + Esc.
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setMenuOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setMenuOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  const menuClass = `split-btn__menu split-btn__menu--${menuDirection}`;

  return (
    <div className={`split-btn-wrap ${className}`.trim()} ref={rootRef}>
      {/* Pill wrapper — overflow:hidden keeps the gradient rounded.
          Menu lives OUTSIDE the pill so it's not clipped by overflow. */}
      <div className={`split-btn split-btn--${size}`}>
        <button
          type="button"
          className="split-btn__main"
          onClick={() => onPrimary && onPrimary()}
          disabled={disabled}
          title={primaryTitle}
          aria-label={ariaLabel || primaryLabel}
        >
          {primaryIcon && <IonIcon icon={primaryIcon} aria-hidden="true" />}
          {primaryLabel}
        </button>
        <button
          type="button"
          className="split-btn__chevron"
          onClick={() => setMenuOpen((v) => !v)}
          disabled={disabled}
          aria-label="More options"
          aria-haspopup="menu"
          aria-expanded={menuOpen}
        >
          <IonIcon icon={chevronDownOutline} aria-hidden="true" />
        </button>
      </div>

      {menuOpen && (
        <div className={menuClass} role="menu">
          {menuHeader && (
            <div className="split-btn__menu-header">
              {menuHeader.icon && <IonIcon icon={menuHeader.icon} aria-hidden="true" />}
              <span>{menuHeader.label}</span>
            </div>
          )}
          {menuHelp && <p className="split-btn__menu-help">{menuHelp}</p>}
          <ul className="split-btn__menu-list">
            {options.map((opt) => (
              <li key={String(opt.value)}>
                <button
                  type="button"
                  role="menuitem"
                  className="split-btn__menu-item"
                  onClick={() => {
                    setMenuOpen(false);
                    if (onSelect) onSelect(opt.value);
                  }}
                  disabled={disabled || opt.disabled}
                >
                  <span className="split-btn__menu-label">{opt.label}</span>
                  {opt.hint && <span className="split-btn__menu-hint">{opt.hint}</span>}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

SplitButton.propTypes = {
  primaryLabel: PropTypes.node.isRequired,
  primaryIcon: PropTypes.any,
  onPrimary: PropTypes.func,
  disabled: PropTypes.bool,
  menuHeader: PropTypes.shape({
    icon: PropTypes.any,
    label: PropTypes.node.isRequired,
  }),
  menuHelp: PropTypes.node,
  options: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    label: PropTypes.node.isRequired,
    hint: PropTypes.node,
    disabled: PropTypes.bool,
  })).isRequired,
  onSelect: PropTypes.func,
  menuDirection: PropTypes.oneOf(['up', 'down']),
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  className: PropTypes.string,
  primaryTitle: PropTypes.string,
  ariaLabel: PropTypes.string,
};

export default SplitButton;
